/*
 * Leaflet facade for the OpenLayers-backed WFS-T client.
 *
 * The class intentionally mimics the small L.WFST surface used by
 * VectorLayerDrawing while keeping WFS transport and feature encoding in
 * framework-neutral modules.
 */
import * as L from "leaflet";
import {WfsTransactionalClient} from "@/engine/wfst/wfsTransactionalClient.js";
import {createWfsFeature} from "@/engine/wfst/wfsFeature.js";
import {normalizeWfsGeometry, WfsEditableFeatureSet} from "@/engine/wfst/wfsEditableFeatureSet.js";

export class LeafletWfstLayerAdapter extends L.FeatureGroup {
    /**
     * Creates a Leaflet FeatureGroup facade backed by WfsTransactionalClient.
     * @param {Object} options L.WFST-compatible options.
     * @param {string} options.url WFS endpoint URL.
     * @param {string} options.typeNS Feature namespace prefix.
     * @param {string} options.typeName Feature type name.
     * @param {string} [options.geometryField="geom"] Geometry property name.
     * @param {Object} [options.crs=L.CRS.EPSG4326] Leaflet CRS descriptor.
     * @param {boolean} [options.forceMulti=false] Whether to force multi line/polygon transactions.
     * @param {boolean} [options.showExisting=true] Whether existing features load immediately.
     * @param {Function|Object} [options.style] Style object or callback for loaded features.
     */
    constructor(options) {
        super();
        this.options = {
            version: "1.1.0",
            geometryField: "geom",
            crs: L.CRS.EPSG4326,
            showExisting: true,
            forceMulti: false,
            ...options,
        };
        this.options.typeNSName = this.namespaceName(this.options.typeName);
        this.options.srsName = this.options.crs.code || "EPSG:4326";
        this.wfst = new WfsTransactionalClient({
            url: this.options.url,
            typeNS: this.options.typeNS,
            typeName: this.options.typeName,
            geometryName: this.options.geometryField,
            version: this.options.version,
            srsName: this.options.srsName,
            transactionSrsName: this.options.transactionSrsName || "CRS:84",
            forceMulti: this.options.forceMulti,
            headers: this.options.headers,
            withCredentials: this.options.withCredentials,
        });
        this.featureSet = new WfsEditableFeatureSet({
            wfst: this.wfst,
            getItemId: (layer) => this.getLayerId(layer),
            featureToItem: (feature) => this._featureToLeafletLayer(feature),
            itemToFeature: (layer) => this._leafletLayerToFeature(layer),
            addItem: (layer) => L.FeatureGroup.prototype.addLayer.call(this, layer),
            removeItem: (layer) => L.FeatureGroup.prototype.removeLayer.call(this, layer),
            prepareLoadedItem: (layer) => this._prepareLoadedLayer(layer),
            onItemLoadError: (error, feature) => {
                console.warn(`WFST: skipped feature ${feature.id || "unknown"} during Leaflet conversion`, error);
            },
        });
        this.state = this.featureSet.state;
        this.changes = this.featureSet.changes;
        this._bridgeFeatureSetEvents();
        this.wfst.ready.then(() => {
            if (this.options.showExisting) {
                this.loadFeatures(this.options.filter);
            } else {
                this.fire("load", {});
            }
        }).catch((error) => this._fireError(error));
    }

    /**
     * Builds a namespace-qualified WFS name.
     * @param {string} name Local feature or property name.
     * @returns {string} Namespace-qualified name.
     */
    namespaceName(name) {
        return `${this.options.typeNS}:${name}`;
    }

    /**
     * Adds a Leaflet layer and marks new layers for insertion.
     * @param {L.Layer} layer Leaflet geometry layer.
     * @returns {LeafletWfstLayerAdapter} This adapter.
     */
    addLayer(layer) {
        this.featureSet.addItem(layer);
        return this;
    }

    /**
     * Removes a Leaflet layer and marks persisted layers for deletion.
     * @param {L.Layer} layer Leaflet geometry layer.
     * @returns {LeafletWfstLayerAdapter} This adapter.
     */
    removeLayer(layer) {
        this.featureSet.removeItem(layer);
        return this;
    }

    /**
     * Marks an existing Leaflet layer for update.
     * @param {L.Layer} layer Leaflet geometry layer.
     * @returns {LeafletWfstLayerAdapter} This adapter.
     */
    editLayer(layer) {
        this.featureSet.editItem(layer);
        return this;
    }

    /**
     * Loads features from WFS and adds them to this FeatureGroup.
     * @param {import("ol/format/filter/Filter.js").default|Array<string>|null} [filter] Optional filter or ids.
     * @returns {Promise<Array<L.Layer>>} Loaded Leaflet layers.
     */
    async loadFeatures(filter = null) {
        try {
            return await this.featureSet.loadFeatures(filter);
        } catch (error) {
            return [];
        }
    }

    /**
     * Saves pending insert, update, and delete operations.
     * @returns {Promise<LeafletWfstLayerAdapter>} This adapter after the transaction completes.
     */
    async save() {
        try {
            await this.featureSet.save();
        } catch (error) {
            error.sourceTarget = this;
        }
        return this;
    }

    /**
     * Bridges renderer-neutral feature-set events to legacy Leaflet events.
     */
    _bridgeFeatureSetEvents() {
        this.featureSet.on("load", ({items}) => {
            this.fire("load", {
                layers: items,
            });
        });
        this.featureSet.on("feature:update", ({items}) => {
            this.fire("wfst:update:success", {
                layers: items,
            });
        });
        this.featureSet.on("feature:delete", ({items}) => {
            this.fire("wfst:delete:success", {
                layers: items,
            });
        });
        this.featureSet.on("feature:insert", ({items, originalItems}) => {
            const layers = items.length > 0 ? items : originalItems;
            this.fire("save:success", {
                layers,
            });
        });
        this.featureSet.on("save:failed", ({error}) => {
            error.sourceTarget = this;
            this.fire("save:failed", error);
        });
        this.featureSet.on("load:failed", ({error}) => {
            this._fireError(error);
        });
    }

    /**
     * Converts a neutral WFS feature into a Leaflet layer.
     * @param {Object} feature Neutral WFS feature.
     * @returns {L.Layer|null} Leaflet geometry layer.
     */
    _featureToLeafletLayer(feature) {
        const geometry = normalizeWfsGeometry(feature.geometry);
        if (!geometry) {
            console.warn(`WFST: feature ${feature.id || "unknown"} has empty or unsupported geometry`);
            return null;
        }
        const geoJsonFeature = {
            type: "Feature",
            id: feature.id || undefined,
            properties: feature.properties || {},
            geometry,
        };
        let layer = null;

        // Circle
        if (geometry.type === "Point" && Number(feature.properties?.radius) > 0) {
            const [lng, lat] = geometry.coordinates;
            layer = L.circle([lat, lng], {
                radius: Number(feature.properties.radius),
            });
        } else {
            layer = L.GeoJSON.geometryToLayer(geoJsonFeature);
        }

        if (!layer) {
            return null;
        }
        layer.feature = geoJsonFeature;
        this.featureSet.ensureFeatureApi(layer);
        return layer;
    }

    /**
     * Applies Leaflet styles to one loaded layer.
     * @param {L.Layer} layer Leaflet geometry layer.
     */
    _prepareLoadedLayer(layer) {
        if (!layer.setStyle) {
            return;
        }
        if (typeof this.options.style === "function") {
            layer.setStyle(this.options.style(layer));
        } else if (this.options.style) {
            layer.setStyle(this.options.style);
        }
    }

    /**
     * Converts a Leaflet layer into a neutral WFS feature.
     * @param {L.Layer} layer Leaflet geometry layer.
     * @returns {Object} Neutral WFS feature.
     */
    _leafletLayerToFeature(layer) {
        const properties = {...(layer.feature?.properties || {})};
        if (layer instanceof L.Circle && typeof layer.getRadius === "function") {
            properties.radius = layer.getRadius();
        }
        return createWfsFeature({
            id: layer.feature?.id || null,
            properties,
            geometry: leafletLayerToGeometry(layer),
            geometryName: this.options.geometryField,
        });
    }

    /**
     * Emits a legacy load error event.
     * @param {Error} error Error raised by the WFS client.
     */
    _fireError(error) {
        this.fire("error", {
            error,
            target: this,
        });
    }
}

/**
 * Converts a Leaflet geometry layer to GeoJSON geometry coordinates.
 * @param {L.Layer} layer Leaflet geometry layer.
 * @returns {Object|null} GeoJSON geometry.
 */
function leafletLayerToGeometry(layer) {
    if (layer instanceof L.Marker || layer instanceof L.Circle) {
        const latLng = normalizeLatLng(layer.getLatLng());
        return {
            type: "Point",
            coordinates: [latLng.lng, latLng.lat],
        };
    }
    if (layer instanceof L.Polygon && !(layer instanceof L.Circle)) {
        return polygonLatLngsToGeometry(layer.getLatLngs());
    }
    if (layer instanceof L.Polyline) {
        return polylineLatLngsToGeometry(layer.getLatLngs());
    }
    return null;
}

/**
 * Converts Leaflet polyline latlngs to GeoJSON LineString or MultiLineString.
 * @param {Array} latLngs Leaflet latlng array.
 * @returns {Object} GeoJSON line geometry.
 */
function polylineLatLngsToGeometry(latLngs) {
    if (isFlatLatLngArray(latLngs)) {
        return {
            type: "LineString",
            coordinates: latLngs.map(latLngToCoordinate),
        };
    }
    return {
        type: "MultiLineString",
        coordinates: latLngs.map((line) => line.map(latLngToCoordinate)),
    };
}

/**
 * Converts Leaflet polygon latlngs to GeoJSON Polygon or MultiPolygon.
 * @param {Array} latLngs Leaflet polygon latlng array.
 * @returns {Object} GeoJSON polygon geometry.
 */
function polygonLatLngsToGeometry(latLngs) {
    if (isFlatLatLngArray(latLngs)) {
        return {
            type: "Polygon",
            coordinates: [latLngs.map(latLngToCoordinate)],
        };
    }
    if (isFlatLatLngArray(latLngs[0])) {
        return {
            type: "Polygon",
            coordinates: latLngs.map((ring) => ring.map(latLngToCoordinate)),
        };
    }
    return {
        type: "MultiPolygon",
        coordinates: latLngs.map((polygon) => polygon.map((ring) => ring.map(latLngToCoordinate))),
    };
}

/**
 * Checks whether an array directly contains Leaflet LatLng objects.
 * @param {Array} value Candidate latlng array.
 * @returns {boolean} True when the array is flat.
 */
function isFlatLatLngArray(value) {
    return Array.isArray(value) && value.length > 0 && !Array.isArray(value[0]);
}

/**
 * Converts a Leaflet LatLng to a GeoJSON coordinate.
 * @param {L.LatLng} latLng Leaflet coordinate.
 * @returns {Array<number>} GeoJSON coordinate.
 */
function latLngToCoordinate(latLng) {
    const normalized = normalizeLatLng(latLng);
    return [normalized.lng, normalized.lat];
}

/**
 * Normalizes longitudes to the world bounds used by GeoServer EPSG:4326 layers.
 * @param {L.LatLng} latLng Leaflet coordinate.
 * @returns {L.LatLng} Normalized coordinate copy.
 */
function normalizeLatLng(latLng) {
    const result = L.latLng(latLng.lat, latLng.lng);
    if (result.lng > 180) {
        result.lng = result.lng - 360 * (Math.floor((result.lng - 180) / 360) + 1);
    } else if (result.lng < -180) {
        result.lng = result.lng + 360 * (Math.floor((-180 - result.lng) / 360) + 1);
    }
    return result;
}
